/**
 * Set Default Facility Modal
 *
 * Confirmation modal for setting a service facility as the org default.
 */

import { Warning as WarningIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';

interface SetDefaultFacilityModalProps {
  open: boolean;
  facility: ServiceFacilityResponse2 | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export function SetDefaultFacilityModal({
  open,
  facility,
  onClose,
  onConfirm,
  isLoading,
}: SetDefaultFacilityModalProps) {
  if (!facility) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Set Default Service Facility?</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <WarningIcon color="warning" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="body1" gutterBottom>
              All claims will use this facility as the service location (Box 32) unless overridden at
              the provider level.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
              Setting <strong>{facility.display_name}</strong> as the default service facility for
              your organization.
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {facility.address?.line1}
                {facility.address?.line2 ? `, ${facility.address.line2}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {facility.address?.city}, {facility.address?.state} {facility.address?.zip}
              </Typography>
              {facility.facility_npi && (
                <Typography variant="caption" color="text.secondary" sx={{display: "block",  mt: 0.5 }}>
                  NPI: {facility.facility_npi}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={isLoading}>
          {isLoading ? 'Setting...' : 'Set as Default'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
